import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DollarSign, Calendar, Layers, AlertCircle, ChevronDown, ChevronUp, User } from 'lucide-react';
import { InfoTooltip } from '../../../common/InfoTooltip';
import { useAuth } from '../../../../contexts/AuthContext';
import CircularProgress from '../../../common/CircularProgress';
import { secureHttp } from '../../../../hooks/api/secureHttp';
import { mapSessionResponse, RawSessionItem } from '../../../../utils/sessionMapper';
import { SESSION_APPOINTMENT_TYPES, LIQUIDACIONES_WEBHOOK } from '../../../../hooks/api/config';
import { parseSessionDate } from '../../../../utils/sessions';
import { BA_TIMEZONE } from '../../../../utils/timezone';
import { periodoParamToLabel, periodoParamToMonth } from '../../../../utils/liquidacionPeriodo';
import { resolveIsRangeView } from '../../../../utils/liquidacionVista';
import { calculatePaymentDueDate } from '../../../../utils/paymentDueDate';
import { usePsychologistContext } from '../../../../contexts/PsychologistContext';
import type { Session } from '../../../../types/session';
import { DatosDeCobroSection } from './DatosDeCobroSection';
import { PaymentDataFormModal } from './PaymentDataFormModal';
import type { LoadedPaymentInfo } from './DatosDeCobroSection';
import { usePaymentInfo } from '../../../../hooks/usePaymentInfo';
import { useMetricsSection } from '../../../../hooks/useMetricsSection';
import { useLiquidacionesBe } from '../../../../hooks/useLiquidacionesBe';
import { usePsychologistFeeType } from '../../../../hooks/usePsychologistFeeType';
import { usePsiPlusHistorial } from '../../../../hooks/usePsiPlusHistorial';
import type { LiquidacionBeBono, LiquidacionBePeriodo, RangePeriodPaymentBe } from '../../../../hooks/api/liquidaciones';
import type { PsiPlusQuarterResultBe } from '../../../../hooks/api/psiPlus';
import { useToast } from '../../../../hooks/useToast';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  RemuneracionZone,
  REMUNERACION_TIERS,
  REMUNERACION_ZONE_LABELS,
} from '../../../../utils/remuneracionTiers';
import { useNZSettlementSchedule } from '../../../../hooks/useNZSettlementSchedule';
import {
  EarlyPaymentInfo,
  EarlyRequestStatus,
  getDefaultVisibleMonth,
  getEarlyRequestStatus,
  getLatestEarlyPayment,
  getLatestEarlyRequestOverall,
  isWithinPaymentWindow,
  NZ_MIN_THRESHOLD,
  NZSettlementMonthResult,
} from '../../../../utils/nzLiquidacion';
import NZEarlyPaymentNotice from './NZEarlyPaymentNotice';
import { solicitarLiquidacionAnticipada } from '../../../../hooks/api/liquidaciones';
import { activityTracker } from '../../../../utils/activityTracker';

interface LiquidacionItem {
  Email: string;
  Tarifa: string;
  Periodo: string;
  Bonificacion: string;
  'Medio de pago': string;
  'Moneda de Pago': string;
  'Tipo de cambio': string;
  'Tipo de servicio': string;
  'Cantidad sesiones': string;
  'Precio por sesión': string;
  'Subtotal por servicio': string;
  'Monto en Moneda de Pago': string;
  'Canceladas ya liquidadas': string;
}

interface PeriodoGroup {
  periodo: string;
  servicios: LiquidacionItem[];
  total: number | null;
  monedaTotal: string;
  montoUSD: number | null;
  tipoCambio: string;
  /** Bonos incluidos en `total` (solo viene de psiplatform-be; el webhook de Make no los trae).
   *  OJO: `total` NO es la suma de `servicios` cuando hay bonos. */
  bonos?: LiquidacionBeBono[];
}

// El webhook devuelve el campo "liquidacion" con comillas sin escapar dentro del string,
// por lo que res.json() falla. Se extrae el objeto JSON crudo por posición de llaves.
// Si el chunk externo falla, avanzamos de a 1 para encontrar el próximo { interno.
function extractJsonObjects(text: string): LiquidacionItem[] {
  const results: LiquidacionItem[] = [];
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf('{', i);
    if (start === -1) break;
    let depth = 0;
    let end = -1;
    for (let j = start; j < text.length; j++) {
      if (text[j] === '{') depth++;
      else if (text[j] === '}') {
        depth--;
        if (depth === 0) { end = j; break; }
      }
    }
    if (end === -1) break;
    const chunk = text.slice(start, end + 1);
    try {
      const obj = JSON.parse(chunk);
      // Nuevo formato de Make: { key, data: LiquidacionItem, __IMTINDEX__, __IMTLENGTH__ }
      // Formato anterior: el objeto plano directamente
      const item: LiquidacionItem = (obj.data?.Email || obj.data?.Periodo) ? obj.data : obj;
      if (item.Email || item.Periodo) {
        results.push(item);
      }
      i = end + 1;
    } catch {
      i = start + 1;
    }
  }
  return results;
}

const MESES: Record<string, number> = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
  julio: 7, agosto: 8, septiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};

function periodoToRango(periodo: string): string {
  // Formato esperado: "Abril 26" → 01/04/2026 al 30/04/2026
  const parts = periodo.trim().split(/\s+/);
  if (parts.length < 2) return periodo;
  const mes = MESES[parts[0].toLowerCase()];
  const anioCorto = parseInt(parts[1], 10);
  if (!mes || isNaN(anioCorto)) return periodo;
  const anio = 2000 + anioCorto;
  const ultimoDia = new Date(anio, mes, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(1)}/${pad(mes)}/${anio} al ${pad(ultimoDia)}/${pad(mes)}/${anio}`;
}

function periodoToISORange(periodo: string): { from: string; to: string } | null {
  const parts = periodo.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const mes = MESES[parts[0].toLowerCase()];
  const anioCorto = parseInt(parts[1], 10);
  if (!mes || isNaN(anioCorto)) return null;
  const anio = 2000 + anioCorto;
  const ultimoDia = new Date(anio, mes, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    from: `${anio}-${pad(mes)}-01 00:00:00`,
    to: `${anio}-${pad(mes)}-${pad(ultimoDia)} 23:59:59`,
  };
}

function groupByPeriodo(items: LiquidacionItem[]): PeriodoGroup[] {
  const map = new Map<string, LiquidacionItem[]>();

  for (const item of items) {
    const key = item.Periodo;
    if (!map.has(key)) map.set(key, []);

    const existing = map.get(key)!;
    // Deduplicar por tipo de servicio dentro del mismo periodo
    const alreadyHasTipo = existing.some(
      (e) => e['Tipo de servicio'] === item['Tipo de servicio']
    );
    if (!alreadyHasTipo) existing.push(item);
  }

  return Array.from(map.entries()).map(([periodo, servicios]) => {
    const primerConMonto = servicios.find((s) => parseFloat(s['Monto en Moneda de Pago']) > 0);
    const total = primerConMonto ? parseFloat(primerConMonto['Monto en Moneda de Pago']) : null;
    const monedaTotal = primerConMonto?.['Moneda de Pago'] ?? '';
    const tipoCambio = primerConMonto?.['Tipo de cambio'] ?? '';

    // Monto en USD = suma de subtotales + bonificaciones + canceladas ya liquidadas del período
    const parseNum = (v: string | number | undefined) => parseFloat(String(v ?? '').replace(/[^0-9.-]/g, '')) || 0;
    const montoUSD = servicios.reduce((acc, s) => {
      return acc + parseNum(s['Subtotal por servicio']) + parseNum(s['Bonificacion']) + parseNum(s['Canceladas ya liquidadas']);
    }, 0);

    return { periodo, servicios, total, monedaTotal, montoUSD: montoUSD > 0 ? montoUSD : null, tipoCambio };
  });
}

/**
 * Convierte un período de psiplatform-be (ya con total/moneda/tipoCambio/montoUSD
 * resueltos por el backend, sin heurísticas) al mismo shape `PeriodoGroup` que arma
 * `groupByPeriodo` para el webhook de Make — así PeriodoCard/ServicioRow renderizan
 * igual sin importar la fuente. Los campos de `LiquidacionItem` que no tiene sentido
 * traer de esta fuente (Email, Tarifa, Medio de pago) quedan vacíos: no se muestran
 * en la UI salvo `Tarifa` (solo para un tooltip de tarifa especial, no crítico acá).
 */
function mapBackendPeriodoToGrupo(periodo: LiquidacionBePeriodo): PeriodoGroup {
  const servicios: LiquidacionItem[] = periodo.servicios.map((s) => ({
    Email: '',
    Tarifa: '',
    Periodo: periodo.periodo,
    Bonificacion: String(s.bonificacion),
    'Medio de pago': '',
    'Moneda de Pago': periodo.moneda,
    'Tipo de cambio': periodo.tipoCambio ?? '',
    'Tipo de servicio': s.tipoServicio,
    'Cantidad sesiones': String(s.cantidadSesiones),
    'Precio por sesión': String(s.precioPorSesion),
    'Subtotal por servicio': String(s.subtotal),
    'Monto en Moneda de Pago': String(periodo.totalACobrar),
    'Canceladas ya liquidadas': String(s.canceladasYaLiquidadas),
  }));

  return {
    periodo: periodo.periodo,
    servicios,
    total: periodo.totalACobrar,
    monedaTotal: periodo.moneda,
    montoUSD: periodo.montoUsd,
    tipoCambio: periodo.tipoCambio ?? '',
    bonos: periodo.bonos ?? [],
  };
}

/**
 * Combina el historial del webhook de Make (`makeGrupos`) con el de la tabla nueva de
 * psiplatform-be (`bePeriodos`), por período. Reemplazo gradual: la tabla nueva arranca
 * vacía y se completa hacia adelante a medida que psima informa cada período, así que un
 * período se sigue mostrando desde Make hasta que psima lo carga ahí — nunca deja de
 * verse ningún período histórico. Si un período existe en ambas fuentes, gana la versión
 * de psiplatform-be (dato estructurado, no depende de parsear el texto crudo de Make).
 */
function mergeLiquidacionPeriodos(
  makeGrupos: PeriodoGroup[],
  bePeriodos: LiquidacionBePeriodo[],
): PeriodoGroup[] {
  const merged = new Map<string, PeriodoGroup>();
  for (const g of makeGrupos) merged.set(g.periodo, g);
  for (const p of bePeriodos) merged.set(p.periodo, mapBackendPeriodoToGrupo(p));
  return Array.from(merged.values());
}

/** Más reciente primero: el mes nuevo que se agrega arriba de todo, sin depender del orden
 *  de llegada de Make/psiplatform-be (que hoy insertan al final). Un período no parseable
 *  (formato inesperado de `periodo`) se manda al final en vez de romper el orden del resto. */
function sortPeriodosDesc(grupos: PeriodoGroup[]): PeriodoGroup[] {
  return [...grupos].sort((a, b) => {
    const rangeA = periodoToISORange(a.periodo);
    const rangeB = periodoToISORange(b.periodo);
    if (!rangeA && !rangeB) return 0;
    if (!rangeA) return 1;
    if (!rangeB) return -1;
    return rangeB.from.localeCompare(rangeA.from);
  });
}

interface Props {
  /** Psicólogas NZ (journey "Tu camino inicial") ven 3 sub-tabs: además de "Tus datos para el
   *  pago" y "Tus liquidaciones", una "Información general" con el bloque estático de la Carta
   *  Oferta — específico de su esquema de remuneración por franjas. El resto ve el mismo patrón
   *  de sub-tabs pero solo esas dos (mismo orden), sin "Información general". */
  isNZ?: boolean;
}

type LiquidacionSubTab = 'info' | 'pago' | 'liquidaciones';

const LIQUIDACION_SUB_TABS: { id: LiquidacionSubTab; label: string }[] = [
  { id: 'liquidaciones', label: 'Tus liquidaciones' },
  { id: 'info', label: 'Información general' },
  { id: 'pago', label: 'Tus datos para el pago' },
];

/** Mismas dos sub-tabs que ven las NZ (mismo orden relativo: liquidaciones, pago), sin
 *  "Información general" — esa tarjeta (RemuneracionInfoCard) es específica del esquema de
 *  remuneración NZ. */
const NON_NZ_LIQUIDACION_SUB_TABS: { id: LiquidacionSubTab; label: string }[] = [
  { id: 'liquidaciones', label: 'Tus liquidaciones' },
  { id: 'pago', label: 'Tus datos para el pago' },
];

export const MetricsLiquidacionTab: React.FC<Props> = ({ isNZ = false }) => {
  const { feeType, loading: feeTypeLoading } = usePsychologistFeeType();
  const isRange = resolveIsRangeView(feeType, isNZ);
  const { user } = useAuth();
  const { showSuccess } = useToast();
  const [makeGrupos, setMakeGrupos] = useState<PeriodoGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // "Tus liquidaciones" es la primera sub-tab (leftmost) para toda audiencia, NZ y no-NZ: es
  // lo que la psicóloga quiere ver apenas entra a "Liquidación".
  // La sub-pestaña vive en la URL (/profile/metricas/liquidacion[/info|/pago]): link directo y pantalla propia en las métricas.
  const [subTab, setSubTab] = useMetricsSection<LiquidacionSubTab>('liquidacion');

  // Historial ya migrado a psiplatform-be. Fuente adicional y todavía incompleta a
  // propósito (ver useLiquidacionesBe): no reemplaza el fetch a Make de abajo, solo se
  // mergea con él — por eso no participa de `loading`/`error` (esos gates son del
  // webhook, que sigue siendo obligatorio hasta discontinuarlo del todo).
  const { periodos: bePeriodos } = useLiquidacionesBe();

  // Bonos PSI+ ya confirmados — se muestran como tarjeta separada junto al período (mes)
  // que cierra cada trimestre, ver PsiPlusBonusCard.
  const { trimestres: psiPlusTrimestres } = usePsiPlusHistorial();

  const {
    info: paymentInfoResponse,
    isLoading: isPaymentInfoLoading,
    error: paymentInfoError,
    refetch: refetchPaymentInfo,
    setInfo: setPaymentInfoResponse,
  } = usePaymentInfo();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const loadedPaymentInfo: LoadedPaymentInfo | null =
    paymentInfoResponse?.currency && paymentInfoResponse.data && paymentInfoResponse.status
      ? { data: paymentInfoResponse.data, status: paymentInfoResponse.status }
      : null;

  // `exists:true` + `currency:null`: medio de pago que esta integración no
  // administra (ej. EUR) — lo gestiona Finanzas directamente, distinto del
  // estado vacío (`exists:false`, sin nada cargado todavía).
  const managedByFinanzas = paymentInfoResponse?.exists === true && paymentInfoResponse.currency === null;

  useEffect(() => {
    if (!user?.email) return;

    setLoading(true);
    setError(false);

    fetch(LIQUIDACIONES_WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        emailPsico: user.email,
        apiKey: process.env.REACT_APP_LIQUIDACIONES_API_KEY ?? '',
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('network error');
        return res.text();
      })
      .then((text) => {
        const items = extractJsonObjects(text);
        setMakeGrupos(groupByPeriodo(items));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [user?.email]);

  const grupos = useMemo(
    () => sortPeriodosDesc(mergeLiquidacionPeriodos(makeGrupos, bePeriodos)),
    [makeGrupos, bePeriodos],
  );

  // Deep-link `?periodo=YYYY-MM` (ej. el de la notificación "Ya está lista tu liquidación de
  // Septiembre 2026"): esa tarjeta se abre sola y se centra en pantalla. Si el período no
  // existe en la lista, simplemente se ve la lista normal.
  const [searchParams] = useSearchParams();
  const periodoEnfocado = periodoParamToLabel(searchParams.get('periodo'));

  const latestPeriodo = grupos.reduce<string | null>((latest, g) => {
    const range = periodoToISORange(g.periodo);
    if (!range) return latest;
    if (!latest) return g.periodo;
    const latestRange = periodoToISORange(latest);
    return !latestRange || range.from > latestRange.from ? g.periodo : latest;
  }, null);

  const liquidacionesList = loading ? (
    <div className="flex justify-center items-center py-20">
      <CircularProgress label="Cargando liquidaciones..." />
    </div>
  ) : error ? (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <div className="bg-red-100 rounded-2xl p-4">
        <AlertCircle size={28} className="text-red-500" />
      </div>
      <p className="text-base font-semibold text-gray-800">No pudimos cargar tus liquidaciones</p>
      <p className="text-sm text-gray-500">Intentá de nuevo en unos minutos.</p>
    </div>
  ) : grupos.length === 0 ? (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <div className="bg-emerald-100 rounded-2xl p-4">
        <DollarSign size={28} className="text-emerald-500" />
      </div>
      <p className="text-base font-semibold text-gray-800">Todavía no tenés liquidaciones</p>
      <p className="text-sm text-gray-500">Acá vas a ver tus cobros cuando estén disponibles.</p>
    </div>
  ) : (
    grupos.map((grupo) => {
      const trimestre = psiPlusTrimestres.find(
        (t) => psiPlusQuarterToPeriodoLabel(t.quarterYear, t.quarter) === grupo.periodo,
      );
      return (
        <React.Fragment key={grupo.periodo}>
          <PeriodoCard
            grupo={grupo}
            isLatest={grupo.periodo === latestPeriodo}
            enfocado={periodoEnfocado !== null && grupo.periodo === periodoEnfocado}
          />
          {/* Si el período ya trae el bono dentro de la liquidación (grupo.bonos), no se muestra
              también la tarjeta aparte del mismo trimestre: la psicóloga lo vería dos veces. */}
          {trimestre && !grupoIncluyeBono(grupo, trimestre) && <PsiPlusBonusCard trimestre={trimestre} />}
        </React.Fragment>
      );
    })
  );

  // Bloque de datos de cobro. Es independiente del journey NZ: toda psicóloga lo ve dentro de
  // la sub-tab "Tus datos para el pago" (NZ y el resto comparten la misma estructura de
  // sub-tabs). Conecta contra psima (GET/POST reales vía psiplatform-be).
  const datosDeCobroBlock = (
    <>
      {isPaymentInfoLoading ? (
        <div className="flex justify-center items-center py-10" data-testid="datos-cobro-loading">
          <CircularProgress label="Cargando datos de cobro..." />
        </div>
      ) : paymentInfoError ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center" data-testid="datos-cobro-error">
          <div className="bg-red-100 rounded-2xl p-4">
            <AlertCircle size={24} className="text-red-500" />
          </div>
          <p className="text-sm font-semibold text-gray-800">No pudimos cargar tus datos de cobro</p>
          <Button variant="outline" size="sm" onClick={refetchPaymentInfo} data-testid="datos-cobro-retry-btn">
            Reintentar
          </Button>
        </div>
      ) : (
        <DatosDeCobroSection
          paymentInfo={loadedPaymentInfo}
          managedByFinanzas={managedByFinanzas}
          // Métricas de Actividad (funnel datos_cobro): abre el formulario; modificar un dato ya cargado
          // cuenta además como "editar".
          onLoad={() => {
            activityTracker.trackAction('payment_info_opened');
            setIsFormOpen(true);
          }}
          onModify={() => {
            activityTracker.trackAction('payment_info_opened');
            activityTracker.trackAction('payment_info_edited');
            setIsFormOpen(true);
          }}
        />
      )}

      <PaymentDataFormModal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        initialData={loadedPaymentInfo}
        onSaved={info => {
          activityTracker.trackAction('payment_info_saved'); // guardó con éxito (el modal llama a onSaved tras el await)
          setPaymentInfoResponse({ exists: true, currency: info.data.currency, status: info.status, data: info.data });
          setIsFormOpen(false);
          showSuccess(
            info.status === 'incomplete'
              ? 'Guardamos tus datos, pero faltan algunos — tu liquidación podría demorarse.'
              : 'Datos de cobro guardados correctamente',
          );
        }}
      />
    </>
  );

  // Gate de la vista range: la TARIFA (`isRange`, ver resolveIsRangeView), no el ranking. No agregar acá
  // un chequeo propio sobre rankingCategory ni sobre el recorrido NZ: el recorrido NZ cambia con el
  // ranking, la tarifa no. (Antes: commit 4e380d1, "O en baja por motivo ajeno a NZ veía igual Camino
  // Inicial y pagos NZ" — con la tarifa como criterio eso tampoco puede pasar.)
  //
  // Mientras no se sabe la tarifa se muestra un loader: evita el parpadeo de la lista equivocada.
  if (feeTypeLoading) {
    return (
      <div className="flex justify-center items-center py-20" data-testid="liquidacion-cargando-tarifa">
        <CircularProgress label="Cargando liquidaciones..." />
      </div>
    );
  }

  const tabs = isRange ? LIQUIDACION_SUB_TABS : NON_NZ_LIQUIDACION_SUB_TABS;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto" data-testid="liquidacion-sub-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSubTab(tab.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              subTab === tab.id
                ? 'bg-violet-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isRange && subTab === 'info' && <RemuneracionInfoCard />}
      {subTab === 'pago' && (
        <div className="flex flex-col gap-5">
          {isRange && <DatosPagoCard />}
          {datosDeCobroBlock}
        </div>
      )}
      {subTab === 'liquidaciones' && (
        <div className="flex flex-col gap-5">
          {isRange ? <NZLiquidacionSchedule onGoToInfo={() => setSubTab('info')} /> : liquidacionesList}
        </div>
      )}
    </div>
  );
};

const REMUNERACION_ZONE_HEADER_STYLES: Record<RemuneracionZone, string> = {
  entrada: 'bg-violet-950 text-white',
  media: 'bg-violet-800 text-white',
  alta: 'bg-emerald-700 text-white',
};

const REMUNERACION_ZONE_PILL_STYLES: Record<RemuneracionZone, string> = {
  entrada: 'bg-violet-100 text-violet-700',
  media: 'bg-violet-100 text-violet-700',
  alta: 'bg-emerald-100 text-emerald-700',
};

const RemuneracionInfoCard: React.FC = () => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
    <div className="flex items-center gap-2 mb-3">
      <div className="bg-emerald-100 rounded-lg p-1.5">
        <DollarSign size={16} className="text-emerald-600" />
      </div>
      <span className="font-semibold text-gray-800 text-base">Remuneración</span>
    </div>
    <p className="text-sm text-gray-600 mb-4">
      Acá va el modelo de pagos. Lo explicamos en detalle porque queremos que entiendas exactamente
      cómo funciona antes de arrancar.
    </p>

    <div className="grid grid-cols-3 gap-3 mb-4">
      {[
        { label: 'Umbral mínimo', value: '15 sesiones' },
        { label: 'Pago inicial', value: '$170 USD' },
        { label: 'Techo', value: '$2.000 USD' },
      ].map((stat) => (
        <div key={stat.label} className="bg-white border border-gray-200 rounded-xl text-center px-2 py-3">
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{stat.label}</p>
          <p className="text-sm font-bold text-gray-900">{stat.value}</p>
        </div>
      ))}
    </div>

    <p className="font-semibold text-gray-900 underline mb-1.5">El modelo es por franja de sesiones tenidas</p>

    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs font-semibold text-gray-700">Sesiones</TableHead>
            <TableHead className="text-xs font-semibold text-gray-700 text-right">Ingreso mensual</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {REMUNERACION_TIERS.map((tier, i) => {
            const prevZone = i > 0 ? REMUNERACION_TIERS[i - 1].zone : null;
            const isTopTier = tier.range === '130+';
            return (
              <React.Fragment key={tier.range}>
                {tier.zone !== prevZone && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      className={`py-2 text-[11px] font-semibold uppercase tracking-wide ${REMUNERACION_ZONE_HEADER_STYLES[tier.zone]}`}
                    >
                      {REMUNERACION_ZONE_LABELS[tier.zone]}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className={isTopTier ? 'bg-emerald-50' : undefined}>
                  <TableCell>
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                        isTopTier ? 'bg-emerald-600 text-white' : REMUNERACION_ZONE_PILL_STYLES[tier.zone]
                      }`}
                    >
                      {tier.range}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-900">{tier.amount} USD</TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>

    <p className="font-semibold text-gray-900 underline mb-1.5">Acumulación</p>
    <p className="text-sm text-gray-600 mb-3">
      Si en un mes hacés menos de 15 sesiones, no se pierden: se suman al mes siguiente. Cuando el
      total acumulado entra en un rango, se liquida ese monto y el contador vuelve a cero.
    </p>
    <div className="bg-gray-50 rounded-lg px-3 py-2.5 mb-3">
      <p className="text-sm text-gray-700">
        <strong className="font-medium text-gray-900">Ejemplo:</strong> en enero hiciste 6 sesiones.
        En febrero hiciste 10. En febrero tenés 16 sesiones acumuladas y cobrás $170.
      </p>
    </div>
    <div className="border-l-2 border-gray-200 pl-3 mb-4">
      <p className="text-sm font-medium text-gray-800 mb-1">¿Y si hago pocas sesiones durante varios meses?</p>
      <p className="text-sm text-gray-600">
        Si después de dos meses completos el acumulado todavía no llegó a 15 sesiones, podés pedir
        una liquidación anticipada. En ese caso, se liquidan $50 por cada 5 sesiones realizadas.
      </p>
    </div>

    <p className="font-semibold text-gray-900 underline mb-1.5">Cuándo y cómo se paga</p>
    <p className="text-sm text-gray-600">
      Al último día de cada mes, el sistema registra en qué franja de sesiones estás. Ese monto se
      liquida dentro de los primeros 5 días del mes siguiente.
    </p>
  </div>
);

const DatosPagoCard: React.FC = () => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
    <div className="flex items-center gap-2">
      <div className="bg-emerald-100 rounded-lg p-1.5">
        <DollarSign size={16} className="text-emerald-600" />
      </div>
      <span className="font-semibold text-gray-800 text-base">Tus datos para el pago</span>
    </div>
  </div>
);

const PSI_PLUS_TIER_NAME: Record<LiquidacionBeBono['tier'], string> = {
  bronze: 'Bronze', silver: 'Silver', gold: 'Gold',
};

function grupoIncluyeBono(grupo: PeriodoGroup, trimestre: PsiPlusQuarterResultBe): boolean {
  return (grupo.bonos ?? []).some(
    (b) => b.tipo === 'PSI_PLUS' && b.quarterYear === trimestre.quarterYear && b.quarter === trimestre.quarter,
  );
}

const formatBonoMonto = (n: number) =>
  n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Línea de un bono PSI+ dentro del detalle de la liquidación — separada de los servicios. */
const BonoRow: React.FC<{ bono: LiquidacionBeBono; moneda: string }> = ({ bono, moneda }) => {
  const esMonedaExtranjera = moneda !== '' && moneda.toUpperCase() !== 'USD';
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-xl bg-violet-50 border border-violet-200 px-4 py-3"
      data-testid="liquidacion-bono-row"
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#2D2049]">
          Bono PSI+ Q{bono.quarter} {bono.quarterYear} — {PSI_PLUS_TIER_NAME[bono.tier]}
        </p>
        <p className="text-xs text-violet-600">Bono trimestral, incluido en el total a cobrar</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-violet-700" data-testid="liquidacion-bono-usd">
          US$ {formatBonoMonto(bono.montoUsd)}
        </p>
        {esMonedaExtranjera && (
          <p className="text-xs text-gray-500">
            {moneda} {formatBonoMonto(bono.montoEnMonedaDePago)}
          </p>
        )}
      </div>
    </div>
  );
};

const PeriodoCard: React.FC<{ grupo: PeriodoGroup; isLatest: boolean; enfocado?: boolean }> = ({
  grupo,
  isLatest,
  enfocado = false,
}) => {
  // `enfocado` (deep-link ?periodo=): arranca abierta y se centra en pantalla al montar.
  const [open, setOpen] = useState(enfocado);
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (enfocado) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [enfocado]);
  const esMonedaExtranjera = grupo.monedaTotal !== '' && grupo.monedaTotal !== 'USD';

  return (
    <div
      ref={cardRef}
      className={`rounded-2xl border bg-white shadow-sm overflow-hidden ${enfocado ? 'border-emerald-300 ring-2 ring-emerald-200' : 'border-gray-100'}`}
      data-testid="liquidacion-periodo-card"
      data-enfocado={enfocado ? 'true' : undefined}
    >
      {/* Header clickeable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <div className="bg-emerald-100 rounded-lg p-1.5">
            <Calendar size={16} className="text-emerald-600" />
          </div>
          <span className="font-semibold text-gray-800 text-base">{grupo.periodo}</span>
        </div>

        <div className="flex items-center gap-4">
          {grupo.total !== null && (
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Total a cobrar</p>
              <p className="text-lg font-bold text-emerald-600" data-testid="liquidacion-total">
                {grupo.monedaTotal} {grupo.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              {/* Monto en USD solo si la moneda de pago no es USD */}
              {esMonedaExtranjera && grupo.montoUSD !== null && (
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-testid="liquidacion-monto-usd">
                  ≈ USD {grupo.montoUSD.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
              {(grupo.bonos ?? []).length > 0 && (
                <p className="text-xs text-violet-600 font-medium mt-0.5" data-testid="liquidacion-incluye-bono">
                  Incluye bono PSI+
                </p>
              )}
              {/* Tipo de cambio solo si moneda ≠ USD */}
              {esMonedaExtranjera && grupo.tipoCambio && (
                <p className="text-xs text-gray-400 mt-0.5" data-testid="liquidacion-tipo-cambio">TC: {grupo.tipoCambio}</p>
              )}
            </div>
          )}
          <div className="text-gray-400">
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </div>
      </button>

      {/* Detalle colapsable */}
      {open && (
        <div className="px-5 py-3 flex flex-col gap-3 border-t border-gray-100">
          {(grupo.bonos ?? []).map((bono) => (
            <BonoRow key={`${bono.quarterYear}-${bono.quarter}`} bono={bono} moneda={grupo.monedaTotal} />
          ))}
          {grupo.servicios.map((servicio) => (
            <ServicioRow key={servicio['Tipo de servicio']} servicio={servicio} periodo={grupo.periodo} isLatest={isLatest} />
          ))}
        </div>
      )}
    </div>
  );
};

const ServicioRow: React.FC<{ servicio: LiquidacionItem; periodo: string; isLatest: boolean }> = ({ servicio, periodo, isLatest }) => {
  const subtotal = servicio['Subtotal por servicio'];
  const bonificacion = servicio['Bonificacion'];
  const rango = periodoToRango(periodo);
  const [expanded, setExpanded] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const { psychologist } = usePsychologistContext();
  // Liquidación en el huso configurado por la psicóloga; Argentina como fallback
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !sessionsLoaded) {
      const range = periodoToISORange(periodo);
      if (!range) { setSessionsLoaded(true); return; }
      setLoadingSessions(true);
      try {
        // El endpoint devuelve un array directo. El filtro `from` no está soportado,
        // se usa solo `to` y se filtra por rango en el cliente.
        const resp = await secureHttp.get<RawSessionItem[] | { results?: RawSessionItem[] }>(
          '/v1/psi-platform/psychologist/sessions',
          { status: 'settled', to: range.to, take: 500 }
        );
        const rawItems: RawSessionItem[] = Array.isArray(resp)
          ? resp
          : Array.isArray((resp as { results?: RawSessionItem[] }).results)
          ? ((resp as { results?: RawSessionItem[] }).results ?? [])
          : [];
        const tipoServicio = servicio['Tipo de servicio'];
        const appointmentType = SESSION_APPOINTMENT_TYPES.find((t) => t.name === tipoServicio);
        const clinicalServiceId = appointmentType?.clinical_service;
        const fromTs = new Date(range.from).getTime();
        const toTs = new Date(range.to).getTime();
        const filtered = rawItems
          .filter((raw) => {
            const proc = raw.process;
            if (typeof proc !== 'object' || proc === null) return false;
            return clinicalServiceId ? proc.type?.id === clinicalServiceId : proc.type?.name === tipoServicio;
          })
          .map(mapSessionResponse)
          .filter((s) => {
            const t = new Date(s.date).getTime();
            return t >= fromTs && t <= toTs;
          })
          .sort((a, b) => a.date.localeCompare(b.date));
        setSessions(filtered);
      } catch {
        setSessions([]);
      } finally {
        setLoadingSessions(false);
        setSessionsLoaded(true);
      }
    }
  };

  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 flex flex-col gap-2" data-testid="liquidacion-servicio-row">
      {/* Tipo de servicio + sesiones */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-violet-100 rounded-md p-1">
            <Layers size={13} className="text-violet-500" />
          </div>
          <span className="text-sm font-semibold text-gray-800" data-testid="liquidacion-servicio-nombre">{servicio['Tipo de servicio']}</span>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center gap-1 text-xs text-violet-600 font-medium hover:text-violet-800 transition-colors"
        >
          {servicio['Cantidad sesiones']} sesiones
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* Detalle financiero */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs pl-1">
        <span className="text-gray-400 flex items-center gap-1">
          Precio por sesión
          {isLatest && servicio['Tarifa'] === 'USD 1' && servicio['Tipo de servicio'] === 'Individual' && (
            <InfoTooltip text="✨ Gracias a tu trayectoria y permanencia en Psi Mammoliti, contás con una tarifa significativamente superior a la base vigente por sesión realizada." />
          )}
          {isLatest && servicio['Tipo de servicio'] === 'Pareja' && parseFloat(String(servicio['Precio por sesión']).replace(/[^0-9.-]/g, '')) === 25 && (
            <InfoTooltip text="✨ Gracias a tu trayectoria y permanencia en Psi Mammoliti, contás con una tarifa significativamente superior a la base vigente por sesión realizada." />
          )}
        </span>
        <span className="font-medium text-right text-gray-700">{servicio['Precio por sesión']}</span>

        <span className="text-gray-400">Subtotal</span>
        <span className="font-medium text-right text-gray-700">{subtotal || '-'}</span>

        {bonificacion && bonificacion !== '0' && bonificacion !== '' && (
          <>
            <span className="text-gray-400">Bonificación</span>
            <span className="font-medium text-right text-emerald-600">{bonificacion}</span>
          </>
        )}
      </div>


      {/* Lista de sesiones expandible */}
      {expanded && (
        <div className="mt-1 border-t border-gray-200 pt-2 flex flex-col gap-1">
          <p className="text-xs text-gray-400 mb-1">Sesiones del {rango}</p>
          {loadingSessions ? (
            <div className="flex justify-center py-3">
              <CircularProgress />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">Sin sesiones registradas</p>
          ) : (
            sessions.map((s) => {
              const patient = s.patients?.[0];
              const patientName = patient
                ? `${patient.name ?? ''} ${(patient as { surname?: string }).surname ?? ''}`.trim()
                : '—';
              const dateObj = parseSessionDate(s.date);
              const dateLabel = dateObj.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: therapistTz });
              const timeLabel = dateObj.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: therapistTz }).replace(/\u202f/g, " ").toLowerCase();
              return (
                <div key={s.id} className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <User size={11} className="text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-700">{patientName}</span>
                  </div>
                  <span className="text-xs text-gray-400">{dateLabel} {timeLabel}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

function formatMonthLabel(month: string): string {
  const [year, monthNum] = month.split('-').map(Number);
  const label = new Date(year, monthNum - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatDueDate(date: Date): string {
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface NZLiquidacionScheduleProps {
  /** Muestra el link "¿Cómo funciona este esquema?" que navega a la sub-tab Información general. */
  onGoToInfo?: () => void;
}

/** Perfil > Tu estadía en Psi Mammoliti > Liquidación > Tus liquidaciones. Se monta solo dentro
 *  del branch `isRange` de MetricsLiquidacionTab (gate por tarifa, ver resolveIsRangeView):
 *  liquidación calculada on-the-fly (useNZSettlementSchedule), con selector para navegar a
 *  meses anteriores desde julio 2026. El mes que se ve por default (sin tocar el selector) lo
 *  decide getDefaultVisibleMonth: el mes anterior hasta el día 10 (se paga a mes vencido, así
 *  que en los primeros días del mes lo relevante es lo que se cobra esos días), el mes en curso
 *  de ahí en más. */
const NZLiquidacionSchedule: React.FC<NZLiquidacionScheduleProps> = ({ onGoToInfo }) => {
  const {
    schedule,
    sessionsByMonth,
    frozenPeriodsByMonth,
    earlyPaymentByMonth,
    latestRequestByMonth,
    refreshRequests,
    loading,
    error,
    refetch,
  } = useNZSettlementSchedule();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Deep-link `?periodo=YYYY-MM` (el de la notificación "Tu liquidación de … está disponible"):
  // si ese mes existe en el selector, arranca ahí en vez del mes por defecto. La elección manual
  // de la psicóloga (selectedMonth) siempre gana. Un valor inválido o un mes que no existe se
  // ignora: comportamiento de siempre.
  const [searchParams] = useSearchParams();
  const deepLinkMonth = periodoParamToMonth(searchParams.get('periodo'));
  const deepLinkValido = deepLinkMonth !== null && schedule.some((r) => r.month === deepLinkMonth);
  const cardRef = useRef<HTMLDivElement>(null);
  const enfocado = deepLinkValido && selectedMonth === null;
  useEffect(() => {
    if (enfocado && !loading) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [enfocado, loading]);

  const defaultMonth = getDefaultVisibleMonth(schedule);
  const activeMonth =
    selectedMonth && schedule.some((r) => r.month === selectedMonth)
      ? selectedMonth
      : deepLinkValido
        ? deepLinkMonth
        : defaultMonth;
  const activeResult = schedule.find((r) => r.month === activeMonth) ?? null;
  // Un mes con pago de tramo ya congelado (verificado contra el recálculo independiente
  // de psiplatform-be) deja de ser una proyección: se muestra ese registro tal cual en
  // vez de recalcularlo — ver NZLiquidacionFrozenMonthCard.
  const frozenRecord = activeMonth ? frozenPeriodsByMonth[activeMonth] : undefined;
  // Anticipada: lo abonado del mes visible se muestra en su tarjeta; el ESTADO DE LA SOLICITUD (enviada,
  // descartada, sin efecto) es global y va arriba del selector — la psicóloga no piensa en meses
  // calendario: si pidió desde noviembre y por defecto ve octubre (se paga a mes vencido), igual lo tiene que ver.
  const activeEarlyPayment = activeMonth ? earlyPaymentByMonth[activeMonth] : undefined;
  const currentMonth = schedule.length > 0 ? schedule[schedule.length - 1].month : null;
  const latestRequest = getLatestEarlyRequestOverall(latestRequestByMonth);
  const latestRequestWithinWindow =
    !!latestRequest && !!currentMonth && isWithinPaymentWindow(latestRequest.month, currentMonth);
  const latestRequestStatus: EarlyRequestStatus = latestRequest
    ? getEarlyRequestStatus(
        schedule.find((r) => r.month === latestRequest.month) ?? { crossedThreshold: false },
        latestRequestWithinWindow,
        latestRequest.request,
        // El último pago de CUALQUIER mes: finanzas puede cargarlo en su período abierto (el mes
        // anterior al del pedido) y igual atiende la solicitud.
        getLatestEarlyPayment(earlyPaymentByMonth)
      )
    : { kind: 'none' };
  // Una vencida de hace meses ya no es noticia: el aviso global solo muestra lo vigente.
  const globalEarlyStatus: EarlyRequestStatus =
    latestRequestStatus.kind === 'expired' || !latestRequestWithinWindow ? { kind: 'none' } : latestRequestStatus;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress label="Cargando tus liquidaciones..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="bg-red-100 rounded-2xl p-4">
          <AlertCircle size={28} className="text-red-500" />
        </div>
        <p className="text-base font-semibold text-gray-800">No pudimos cargar tus liquidaciones</p>
        <p className="text-sm text-gray-500">Intentá de nuevo en unos minutos.</p>
        <button
          type="button"
          onClick={refetch}
          className="mt-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          data-testid="nz-liquidacion-reintentar"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!activeResult) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <div className="bg-emerald-100 rounded-2xl p-4">
          <DollarSign size={28} className="text-emerald-500" />
        </div>
        <p className="text-base font-semibold text-gray-800">Todavía no tenés liquidaciones</p>
        <p className="text-sm text-gray-500">Acá vas a ver tus cobros cuando estén disponibles.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="nz-liquidacion-schedule">
      <NZEarlyPaymentNotice
        status={globalEarlyStatus}
        requestMonthLabel={latestRequest ? formatMonthLabel(latestRequest.month) : undefined}
      />

      <Select value={activeMonth ?? undefined} onValueChange={setSelectedMonth}>
        <SelectTrigger className="w-full sm:w-64" data-testid="nz-liquidacion-mes-selector">
          <SelectValue placeholder="Seleccionar mes" />
        </SelectTrigger>
        <SelectContent>
          {[...schedule].reverse().map((r) => (
            <SelectItem key={r.month} value={r.month}>
              {formatMonthLabel(r.month)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div
        ref={cardRef}
        className={enfocado ? 'rounded-2xl ring-2 ring-emerald-200' : undefined}
        data-testid="nz-liquidacion-tarjeta-contenedor"
        data-enfocado={enfocado ? 'true' : undefined}
      >
        {frozenRecord ? (
          <NZLiquidacionFrozenMonthCard month={activeMonth as string} record={frozenRecord} />
        ) : (
          <NZLiquidacionMonthCard
            result={activeResult}
            schedule={schedule}
            sessionsByMonth={sessionsByMonth}
            earlyPayment={activeEarlyPayment}
            hasPendingRequest={globalEarlyStatus.kind === 'pending'}
            currentMonth={currentMonth}
            onGoToMonth={setSelectedMonth}
            onRequested={refreshRequests}
          />
        )}
      </div>

      {onGoToInfo && (
        <button
          type="button"
          onClick={onGoToInfo}
          className="text-sm text-violet-600 font-medium hover:text-violet-800 hover:underline transition-colors text-left"
          data-testid="nz-liquidacion-como-funciona"
        >
          ¿Cómo funciona este esquema?
        </button>
      )}
    </div>
  );
};

/**
 * Mes con un pago de tramo ya congelado — a diferencia de `NZLiquidacionMonthCard`, no
 * es una proyección calculada en vivo: es un hecho ya verificado contra finanzas-gitlab
 * (ver useNZSettlementSchedule.frozenPeriodsByMonth / LiquidacionesService.congelarRangePayment),
 * así que no hay estado "Acumulando"/"Por pagar" posible para un mes que ya tiene este
 * registro y los números vienen tal cual del backend, sin recalcular nada del lado del cliente.
 *
 * Dos estados, decididos por `record.paidAt`: con fecha → "Pagada"; sin fecha (finanzas-gitlab
 * informó la liquidación antes de marcar el pago) → "Liquidación lista, pago pendiente", con la
 * fecha límite de pago calculada (día 5 del mes siguiente, corrido al próximo día hábil — misma
 * regla que el resto de la pantalla). Cuando finanzas marca el pago, reenvía el registro con
 * `paidAt` y la tarjeta pasa sola a "Pagada".
 */
const NZLiquidacionFrozenMonthCard: React.FC<{ month: string; record: RangePeriodPaymentBe }> = ({ month, record }) => {
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const esMonedaExtranjera = record.currency !== 'USD';
  const netSessionsThisPeriod = Math.max(0, record.sessionsCount - record.earlyPaymentSessionsPaid);
  const isPaid = record.paidAt !== null;
  // Fecha límite de pago del mes liquidado (solo se usa sin paidAt).
  const dueDate = calculatePaymentDueDate(new Date(record.periodYear, record.periodMonth - 1, 1));

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5 flex flex-col gap-4" data-testid="nz-liquidacion-frozen-mes-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`rounded-lg p-1.5 ${isPaid ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            <Calendar size={16} className={isPaid ? 'text-emerald-600' : 'text-amber-600'} />
          </div>
          <span className="font-semibold text-gray-800 text-base" data-testid="nz-liquidacion-mes-label">
            {formatMonthLabel(month)}
          </span>
        </div>
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold cursor-help ${
                isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}
              data-testid="nz-liquidacion-status-badge"
            >
              {isPaid ? 'Pagada' : 'Pago pendiente'}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="w-[240px] py-2 leading-relaxed" data-testid="nz-liquidacion-status-tooltip">
            {isPaid
              ? 'Este pago ya fue verificado y confirmado — no es una proyección. Según tu método de cobro, puede demorar entre 24 y 72hs en verse reflejado.'
              : 'Esta liquidación ya fue verificada y el monto es definitivo — no es una proyección. Todavía no se procesó el pago.'}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-xl px-3 py-2.5">
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Sesiones del período</p>
          <p className="text-sm font-bold text-gray-900" data-testid="nz-liquidacion-sesiones">{record.sessionsCount}</p>
        </div>
        <div className="bg-gray-50 rounded-xl px-3 py-2.5">
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Tramo</p>
          <p className="text-sm font-bold text-gray-900">{record.tierLabel}</p>
        </div>
      </div>

      {record.earlyPaymentSessionsPaid > 0 && (
        <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          De las {record.sessionsCount} sesiones, {record.earlyPaymentSessionsPaid} ya se habían cobrado
          como anticipada — este pago corresponde a las {netSessionsThisPeriod} restantes.
        </p>
      )}

      <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
        <span className="text-sm text-gray-600">{isPaid ? 'Monto pagado' : 'Monto a pagar'}</span>
        <div className="text-right">
          <span className="text-lg font-bold text-emerald-600" data-testid="nz-liquidacion-monto">
            {record.currency} {record.montoEnMonedaDePago.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          {esMonedaExtranjera && (
            <p className="text-xs text-gray-500 font-medium mt-0.5" data-testid="nz-liquidacion-monto-usd">
              ≈ USD {record.montoUsd.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {record.exchangeRate && ` (TC: ${record.exchangeRate})`}
            </p>
          )}
        </div>
      </div>

      {record.paidAt !== null ? (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Fecha de pago</span>
          <span className="font-medium text-gray-800" data-testid="nz-liquidacion-fecha-pago">
            {formatDueDate(new Date(record.paidAt))}
          </span>
        </div>
      ) : (
        <div className="rounded-xl bg-amber-50 px-3 py-2.5 flex flex-col gap-1" data-testid="nz-liquidacion-pago-pendiente">
          <p className="text-sm font-semibold text-amber-800">Liquidación lista, pago pendiente</p>
          <p className="text-xs text-amber-700" data-testid="nz-liquidacion-fecha-pago">
            Los pagos se procesan hasta el día 5 del mes siguiente. Si el día 5 coincide con un fin de semana o feriado,
            el pago se realiza el primer día hábil posterior. Fecha estimada: {formatDueDate(dueDate)}.
          </p>
        </div>
      )}

      <div className="bg-gray-50 rounded-xl px-3 py-2.5" data-testid="nz-liquidacion-frozen-detalle-container">
        <button
          type="button"
          onClick={() => setSessionsOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-left"
          data-testid="nz-liquidacion-frozen-detalle-toggle"
          aria-expanded={sessionsOpen}
        >
          <p className="text-xs text-gray-600 font-medium">{record.sesiones.length} sesiones que integraron este pago</p>
          {sessionsOpen ? <ChevronUp size={14} className="text-gray-500 shrink-0" /> : <ChevronDown size={14} className="text-gray-500 shrink-0" />}
        </button>

        {sessionsOpen && (
          <div className="mt-2 pt-2 border-t border-gray-200 flex flex-col gap-0.5" data-testid="nz-liquidacion-frozen-detalle">
            {record.sesiones.map((s) => {
              const dateObj = parseSessionDate(s.sessionDate);
              const dateLabel = dateObj.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: therapistTz });
              return (
                <div key={s.sessionId} className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-1.5">
                    <User size={11} className="text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-700">{s.patientNames.join(', ') || '—'}</span>
                  </div>
                  <span className="text-xs text-gray-400">{dateLabel}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/** Marzo/Junio/Septiembre/Diciembre — mes de cierre de cada trimestre calendario, mismo
 * criterio acordado con finanzas-gitlab (el pago se registra en el mes que cierra el
 * trimestre). Mapea a un `PeriodoGroup.periodo` (ej. "Septiembre 26") para poder ubicar
 * la tarjeta de bono PSI+ junto al período mensual que corresponde. */
const QUARTER_CLOSING_MONTH_NAME: Record<number, string> = {
  1: 'Marzo', 2: 'Junio', 3: 'Septiembre', 4: 'Diciembre',
};

function psiPlusQuarterToPeriodoLabel(quarterYear: number, quarter: number): string {
  const anioCorto = String(quarterYear % 100).padStart(2, '0');
  return `${QUARTER_CLOSING_MONTH_NAME[quarter]} ${anioCorto}`;
}

const PSI_PLUS_TIER_EMOJI: Record<PsiPlusQuarterResultBe['tierLabel'], string> = {
  bronze: '🥉', silver: '🥈', gold: '🥇',
};

/**
 * Bono trimestral PSI+ ya confirmado, mostrado como tarjeta SEPARADA dentro del período
 * (mes) que cierra el trimestre — mismo criterio que NZLiquidacionFrozenMonthCard para el
 * tramo "range". Es el camino de respaldo: solo se muestra cuando el período NO trae el bono
 * dentro de la liquidación (`grupo.bonos`, ver BonoRow), para que la psicóloga no lo vea dos
 * veces — mientras finanzas-gitlab no informe el bono en el período, esta tarjeta sigue siendo
 * lo único que lo muestra.
 */
const PsiPlusBonusCard: React.FC<{ trimestre: PsiPlusQuarterResultBe }> = ({ trimestre }) => {
  const esMonedaExtranjera = trimestre.currency.toUpperCase() !== 'USD';

  return (
    <div
      className="rounded-2xl border-2 border-violet-200 bg-violet-50 shadow-sm p-5 flex items-center gap-4"
      data-testid="psi-plus-bonus-card"
    >
      <span className="text-3xl shrink-0">{PSI_PLUS_TIER_EMOJI[trimestre.tierLabel]}</span>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <p className="text-xs font-bold uppercase tracking-widest text-violet-500">
          Bono trimestral PSI+ — Q{trimestre.quarter} {trimestre.quarterYear}
        </p>
        <p className="text-lg font-bold text-[#2D2049]">
          {trimestre.currency.toUpperCase()}{' '}
          {trimestre.montoEnMonedaDePago.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        {esMonedaExtranjera && (
          <p className="text-xs text-violet-600">
            ≈ USD {trimestre.montoUsd.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">Pagado el {formatDueDate(new Date(trimestre.paidAt))}</p>
      </div>
    </div>
  );
};

type NZStatusBadgeKey = 'accumulating' | 'pending' | 'paid';

const NZ_STATUS_BADGE: Record<NZStatusBadgeKey, { label: string; className: string; description: string }> = {
  accumulating: {
    label: 'Acumulando',
    className: 'bg-amber-100 text-amber-700',
    description: 'Todavía estás sumando sesiones para esta liquidación. Se acumulan mes a mes hasta llegar al mínimo de 15.',
  },
  pending: {
    label: 'Por pagar',
    className: 'bg-red-100 text-red-700',
    description: 'Ya se calculó el monto de este período. Se gestiona dentro de los primeros días del mes siguiente.',
  },
  paid: {
    label: 'Pagada',
    className: 'bg-emerald-100 text-emerald-700',
    description: 'Este pago ya fue gestionado de nuestro lado. Según tu método de cobro, puede demorar entre 24 y 72hs en verse reflejado.',
  },
};

const NZLiquidacionMonthCard: React.FC<{
  result: NZSettlementMonthResult;
  schedule: NZSettlementMonthResult[];
  sessionsByMonth: Record<string, Session[]>;
  earlyPayment?: EarlyPaymentInfo;
  /** Hay una solicitud de anticipada pendiente (de cualquier mes): no se ofrece pedir otra. */
  hasPendingRequest: boolean;
  /** Mes en curso ('YYYY-MM') — el único donde se puede pedir la anticipada. */
  currentMonth: string | null;
  onGoToMonth: (month: string) => void;
  onRequested: () => Promise<void>;
}> = ({ result, schedule, sessionsByMonth, earlyPayment, hasPendingRequest, currentMonth, onGoToMonth, onRequested }) => {
  const badgeKey: NZStatusBadgeKey = !result.crossedThreshold ? 'accumulating' : result.status;
  const badge = NZ_STATUS_BADGE[badgeKey];

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5 flex flex-col gap-4" data-testid="nz-liquidacion-mes-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-emerald-100 rounded-lg p-1.5">
            <Calendar size={16} className="text-emerald-600" />
          </div>
          <span className="font-semibold text-gray-800 text-base" data-testid="nz-liquidacion-mes-label">
            {formatMonthLabel(result.month)}
          </span>
        </div>
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold cursor-help ${badge.className}`}
              data-testid="nz-liquidacion-status-badge"
            >
              {badge.label}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="w-[240px] py-2 leading-relaxed" data-testid="nz-liquidacion-status-tooltip">
            {badge.description}
          </TooltipContent>
        </Tooltip>
      </div>

      {result.crossedThreshold && result.tier ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl px-3 py-2.5">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Sesiones del mes</p>
              <p className="text-sm font-bold text-gray-900" data-testid="nz-liquidacion-sesiones">{result.sessionsThisMonth}</p>
            </div>
            <div className="bg-gray-50 rounded-xl px-3 py-2.5">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Acumulado considerado</p>
              <p className="text-sm font-bold text-gray-900">{result.totalConsideredForThreshold}</p>
            </div>
          </div>
          {result.earlyPaymentSessionsPaid > 0 && (
            <p className="text-xs text-gray-600" data-testid="nz-liquidacion-neto-nota">
              De las {result.totalConsideredForThreshold} sesiones, {result.earlyPaymentSessionsPaid} ya se cobraron
              como anticipada — esta liquidación corresponde a las {result.netConsideredForThreshold} restantes.
            </p>
          )}
          <NZEarlyPaymentNotice payment={earlyPayment} />
          <NZLiquidadoSesionesDetalle result={result} schedule={schedule} sessionsByMonth={sessionsByMonth} />
          <div>
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${REMUNERACION_ZONE_HEADER_STYLES[result.tier.zone]}`}
              data-testid="nz-liquidacion-zona-pill"
            >
              {REMUNERACION_ZONE_LABELS[result.tier.zone]}
            </span>
          </div>
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
            <span className="text-sm text-gray-600">Monto a liquidar</span>
            <span className="text-lg font-bold text-emerald-600" data-testid="nz-liquidacion-monto">
              USD {result.amountToSettle?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Fecha de pago</span>
            <span className="font-medium text-gray-800" data-testid="nz-liquidacion-fecha-pago">
              {formatDueDate(result.dueDate)}
            </span>
          </div>
        </>
      ) : (
        <NZAcumulandoZonasView
          result={result}
          schedule={schedule}
          sessionsByMonth={sessionsByMonth}
          earlyPayment={earlyPayment}
          hasPendingRequest={hasPendingRequest}
          currentMonth={currentMonth}
          onGoToMonth={onGoToMonth}
          onRequested={onRequested}
        />
      )}
    </div>
  );
};

// Debajo de este número de sesiones faltantes para el umbral mínimo, se muestra el incentivo
// visual ("¡Estás cerca!"). Puramente de UI — no es parte de la regla de negocio de
// calculateNZSettlementSchedule, por eso vive acá y no en nzLiquidacion.ts.
const NZ_CLOSE_TO_THRESHOLD_SESSIONS = 5;

/** Meses ('YYYY-MM', orden cronológico) que componen el acumulado vigente en `result`: se
 *  camina hacia atrás desde `result.month` mientras el mes anterior NO haya cruzado ningún
 *  umbral — cruzar resetea remainingCarryOver a 0 (ver calculateNZSettlementSchedule), así que
 *  ahí termina la racha. Sirve tanto para un mes todavía "Acumulando" (ningún mes de la racha
 *  cruzó, incluyendo `result`) como para uno recién liquidado/pagado (`result` sí cruzó, pero
 *  los meses anteriores de la racha son los que aportaron el carryOver que integró el pago). No
 *  usa `result.monthsWithoutThreshold` a propósito: ese contador se resetea a 0 justo en el mes
 *  que cruza, así que no sirve para reconstruir la racha de un mes liquidado. */
function getStreakMonths(schedule: NZSettlementMonthResult[], result: NZSettlementMonthResult): string[] {
  const index = schedule.findIndex((r) => r.month === result.month);
  if (index === -1) return [result.month];
  let start = index;
  while (start > 0 && !schedule[start - 1].crossedThreshold) {
    start -= 1;
  }
  return schedule.slice(start, index + 1).map((r) => r.month);
}

/** "Julio de 2026" / "Julio de 2026 y Agosto de 2026" / "Julio de 2026, Agosto de 2026 y
 *  Septiembre de 2026" — para enumerar en prosa los meses que aportaron carryOver. */
function joinMonthLabels(months: string[]): string {
  const labels = months.map(formatMonthLabel);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`;
}

/**
 * Detalle expandible de qué sesiones componen el acumulado de un mes YA LIQUIDADO (cruzó
 * umbral: "Por pagar"/"Pagada") — separado de NZAcumulandoZonasView (que resuelve el mismo
 * problema visual para un mes todavía acumulando) a propósito: mientras acumula el detalle
 * sesión por sesión es solo informativo, pero una vez liquidado importa poder auditar qué
 * sesión puntual integró ese pago — mismo criterio que finanzas-gitlab (RangePaymentHistorySheet,
 * columna "Período de origen": "Este período" vs "Arrastre"), acá aplicado como agrupación por
 * mes de origen en vez de una columna por sesión, porque ya se agrupa así en `sessionsByMonth`.
 */
const NZLiquidadoSesionesDetalle: React.FC<{
  result: NZSettlementMonthResult;
  schedule: NZSettlementMonthResult[];
  sessionsByMonth: Record<string, Session[]>;
}> = ({ result, schedule, sessionsByMonth }) => {
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const [open, setOpen] = useState(false);

  const streakMonths = getStreakMonths(schedule, result);
  const carriedMonths = streakMonths.slice(0, -1);
  const hasCarryOver = result.carriedOverFromPreviousMonths > 0;

  const summaryLabel = result.earlyPaymentSessionsPaid > 0
    ? `${result.totalConsideredForThreshold} sesiones del período: ${result.netConsideredForThreshold} se liquidan acá y ${result.earlyPaymentSessionsPaid} ya se cobraron como anticipada`
    : hasCarryOver
    ? `${result.totalConsideredForThreshold} sesiones liquidadas: ${result.sessionsThisMonth} de este mes + ${result.carriedOverFromPreviousMonths} acumuladas de ${joinMonthLabels(carriedMonths)}`
    : `${result.sessionsThisMonth} ${result.sessionsThisMonth === 1 ? 'sesión' : 'sesiones'} de este mes`;

  return (
    <div className="bg-gray-50 rounded-xl px-3 py-2.5" data-testid="nz-liquidacion-liquidado-detalle-container">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="nz-liquidacion-liquidado-detalle-toggle"
        aria-expanded={open}
      >
        <p className="text-xs text-gray-600 font-medium" data-testid="nz-liquidacion-liquidado-detalle-resumen">
          {summaryLabel}
        </p>
        {open ? (
          <ChevronUp size={14} className="text-gray-500 shrink-0" />
        ) : (
          <ChevronDown size={14} className="text-gray-500 shrink-0" />
        )}
      </button>

      {open && (
        <div className="mt-2 pt-2 border-t border-gray-200 flex flex-col gap-2" data-testid="nz-liquidacion-liquidado-detalle">
          {streakMonths.map((month) => {
            const monthSessions = sessionsByMonth[month] ?? [];
            const isCurrentMonth = month === result.month;
            return (
              <div key={month}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  {formatMonthLabel(month)}
                  <span className="normal-case font-normal text-gray-400"> · {isCurrentMonth ? 'este mes' : 'acumulada'}</span>
                </p>
                {monthSessions.length === 0 ? (
                  <p className="text-xs text-gray-400 pl-1">Sin sesiones registradas</p>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {monthSessions.map((s) => {
                      const patient = s.patients?.[0];
                      const patientName = patient?.name?.trim() || '—';
                      const dateObj = parseSessionDate(s.date);
                      const dateLabel = dateObj.toLocaleDateString('es-AR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        timeZone: therapistTz,
                      });
                      return (
                        <div
                          key={s.id}
                          className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <User size={11} className="text-gray-400 shrink-0" />
                            <span className="text-xs text-gray-700">{patientName}</span>
                          </div>
                          <span className="text-xs text-gray-400">{dateLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Mientras un mes está "Acumulando" (no cruzó ningún umbral todavía), la próxima zona a
 *  alcanzar es SIEMPRE la primera de REMUNERACION_TIERS ('15 – 19') — ese es justamente el
 *  único tier que arranca en el umbral mínimo (15), y crossedThreshold=false implica que el
 *  acumulado todavía no llega ahí. No hace falta buscarla: no existe otro tier alcanzable
 *  todavía. Replica visualmente la tabla de RemuneracionInfoCard (mismo REMUNERACION_TIERS,
 *  mismos estilos de sección) resaltando esa primera fila con el progreso integrado, y
 *  atenuando el resto de las zonas debajo — Sesiones del mes/Acumulado considerado quedan
 *  integrados en el texto de progreso en vez de mostrarse en cajas aparte. */
const NZAcumulandoZonasView: React.FC<{
  result: NZSettlementMonthResult;
  schedule: NZSettlementMonthResult[];
  sessionsByMonth: Record<string, Session[]>;
  earlyPayment?: EarlyPaymentInfo;
  hasPendingRequest: boolean;
  currentMonth: string | null;
  onGoToMonth: (month: string) => void;
  onRequested: () => Promise<void>;
}> = ({ result, schedule, sessionsByMonth, earlyPayment, hasPendingRequest, currentMonth, onGoToMonth, onRequested }) => {
  const { psychologist } = usePsychologistContext();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const [sessionsOpen, setSessionsOpen] = useState(false);

  // El progreso hacia la franja es sobre el acumulado NETO: lo ya cobrado como anticipada no cuenta.
  const remaining = Math.max(0, NZ_MIN_THRESHOLD - result.netConsideredForThreshold);
  const progressPct = Math.max(0, Math.min(100, Math.round((result.netConsideredForThreshold / NZ_MIN_THRESHOLD) * 100)));
  const isCloseToThreshold = remaining > 0 && remaining <= NZ_CLOSE_TO_THRESHOLD_SESSIONS;
  const sesionLabel = remaining === 1 ? 'sesión' : 'sesiones';
  const faltaLabel = remaining === 1 ? 'falta' : 'faltan';
  const streakMonths = getStreakMonths(schedule, result);
  // Mes cerrado que sigue acumulando (el acumulado continúa en el mes en curso): se ofrece ir ahí.
  const isPastMonth = !!currentMonth && result.month !== currentMonth;

  return (
    <div className="flex flex-col gap-3" data-testid="nz-liquidacion-acumulando">
      <p className="text-sm text-amber-800">
        Cada sesión te acerca a tu próxima liquidación — seguís sumando hasta llegar a la zona de entrada.
      </p>

      {/* Arriba de la tabla de franjas (larga): la solicitud/pago de anticipada tiene que verse sin scrollear. */}
      <NZEarlyPaymentNotice payment={earlyPayment} />

      {isPastMonth && currentMonth && (
        <div
          className="flex flex-col items-start gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
          data-testid="nz-liquidacion-puente-mes-en-curso"
        >
          <p className="text-sm text-violet-900">
            Tu acumulado sigue sumando en <strong>{formatMonthLabel(currentMonth)}</strong>.
          </p>
          <button
            type="button"
            onClick={() => onGoToMonth(currentMonth)}
            className="text-left text-sm font-semibold text-violet-700 hover:text-violet-900 hover:underline sm:shrink-0"
            data-testid="nz-liquidacion-puente-ir"
          >
            Ver {formatMonthLabel(currentMonth)}
          </button>
        </div>
      )}

      {isCloseToThreshold && (
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 flex items-center gap-2"
          data-testid="nz-liquidacion-incentivo"
        >
          <span aria-hidden="true" className="text-base">🔥</span>
          <p className="text-sm font-semibold text-emerald-800">
            ¡Estás cerca! Te {faltaLabel} {remaining} {sesionLabel} para tu próxima liquidación.
          </p>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-semibold text-gray-700">Sesiones</TableHead>
              <TableHead className="text-xs font-semibold text-gray-700 text-right">Ingreso mensual</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {REMUNERACION_TIERS.map((tier, i) => {
              const isNextZone = i === 0;
              const prevZone = i > 0 ? REMUNERACION_TIERS[i - 1].zone : null;
              return (
                <React.Fragment key={tier.range}>
                  {tier.zone !== prevZone && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        className={`py-2 text-[11px] font-semibold uppercase tracking-wide ${
                          isNextZone ? REMUNERACION_ZONE_HEADER_STYLES[tier.zone] : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {REMUNERACION_ZONE_LABELS[tier.zone]}
                      </TableCell>
                    </TableRow>
                  )}
                  {isNextZone ? (
                    <TableRow>
                      <TableCell colSpan={2} className="p-0">
                        <div
                          className="px-3 py-3 bg-violet-50 border-l-4 border-violet-600"
                          data-testid="nz-liquidacion-proxima-zona"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-600 text-white"
                              data-testid="nz-liquidacion-zona-objetivo"
                            >
                              {tier.range}
                            </span>
                            <span className="text-sm font-bold text-gray-900">{tier.amount} USD</span>
                          </div>
                          <div
                            className="w-full bg-violet-100 rounded-full h-2 overflow-hidden mb-1.5"
                            data-testid="nz-liquidacion-progreso-barra"
                            data-progress={progressPct}
                            role="progressbar"
                            aria-valuenow={progressPct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Progreso hacia el umbral mínimo de ${NZ_MIN_THRESHOLD} sesiones`}
                          >
                            <div className="bg-violet-600 h-2 rounded-full" style={{ width: `${progressPct}%` }} />
                          </div>

                          <button
                            type="button"
                            onClick={() => setSessionsOpen((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 text-left"
                            data-testid="nz-liquidacion-progreso-toggle"
                            aria-expanded={sessionsOpen}
                          >
                            <p className="text-xs text-violet-700 font-medium" data-testid="nz-liquidacion-progreso-texto">
                              Llevás {result.netConsideredForThreshold} de {NZ_MIN_THRESHOLD} sesiones
                              {(result.carriedOverFromPreviousMonths > 0 || result.earlyPaymentSessionsPaid > 0) && (
                                <span className="text-violet-500">
                                  {' '}({result.sessionsThisMonth} este mes
                                  {result.carriedOverFromPreviousMonths > 0 &&
                                    ` + ${result.carriedOverFromPreviousMonths} acumuladas`}
                                  {result.earlyPaymentSessionsPaid > 0 &&
                                    ` − ${result.earlyPaymentSessionsPaid} ya cobradas como anticipada`})
                                </span>
                              )}
                            </p>
                            {sessionsOpen ? (
                              <ChevronUp size={14} className="text-violet-500 shrink-0" />
                            ) : (
                              <ChevronDown size={14} className="text-violet-500 shrink-0" />
                            )}
                          </button>

                          {sessionsOpen && (
                            <div className="mt-2 pt-2 border-t border-violet-200 flex flex-col gap-2" data-testid="nz-liquidacion-sesiones-detalle">
                              {streakMonths.map((month) => {
                                const monthSessions = sessionsByMonth[month] ?? [];
                                return (
                                  <div key={month}>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 mb-1">
                                      {formatMonthLabel(month)}
                                    </p>
                                    {monthSessions.length === 0 ? (
                                      <p className="text-xs text-violet-400 pl-1">Sin sesiones registradas</p>
                                    ) : (
                                      <div className="flex flex-col gap-0.5">
                                        {monthSessions.map((s) => {
                                          const patient = s.patients?.[0];
                                          const patientName = patient?.name?.trim() || '—';
                                          const dateObj = parseSessionDate(s.date);
                                          const dateLabel = dateObj.toLocaleDateString('es-AR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            timeZone: therapistTz,
                                          });
                                          return (
                                            <div
                                              key={s.id}
                                              className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-violet-100/60 transition-colors"
                                            >
                                              <div className="flex items-center gap-1.5">
                                                <User size={11} className="text-violet-400 shrink-0" />
                                                <span className="text-xs text-violet-700">{patientName}</span>
                                              </div>
                                              <span className="text-xs text-violet-400">{dateLabel}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow className="opacity-50">
                      <TableCell>
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-400">
                          {tier.range}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-400">{tier.amount} USD</TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <SolicitarAnticipadaButton
        result={result}
        hasPendingRequest={hasPendingRequest}
        isCurrentMonth={!isPastMonth}
        onRequested={onRequested}
      />
    </div>
  );
};

/**
 * CTA para pedir la liquidación anticipada. Solo visible cuando
 * `result.earlyLiquidationEligible` (2 meses consecutivos sin cruzar umbral) y hay algo
 * real que pedir (`earlyLiquidationAmount > 0` — puede dar 0 si ya se pagó como anticipada
 * todo lo disponible en múltiplos de 5, ver calculateNZSettlementSchedule).
 *
 * No persiste nada ni valida contra sesiones reales: dispara un mail a
 * administración/liquidaciones (ver LiquidacionesService.solicitarLiquidacionAnticipada)
 * para que finanzas-gitlab la gestione a mano, igual que hoy sin ningún canal formal. Una
 * vez enviada queda deshabilitado por el resto de esta carga de la página (estado local,
 * no persistido) — evita el reenvío accidental por doble click, no es un límite real de
 * "una sola solicitud por mes" (recargar la página lo vuelve a habilitar a propósito: la
 * psicóloga puede seguir pidiendo si todavía no la contactaron).
 */
const SolicitarAnticipadaButton: React.FC<{
  result: NZSettlementMonthResult;
  /** Ya hay una solicitud de este mes sin abonar: el aviso persistente la muestra y no se
   *  ofrece pedir otra encima (evita solicitudes duplicadas del mismo bloque de sesiones). */
  hasPendingRequest: boolean;
  /** El mes mostrado es el mes en curso: único lugar donde se ofrece pedir (o se explica por qué no). */
  isCurrentMonth: boolean;
  /** Refresca las solicitudes para que el aviso aparezca apenas se envía. */
  onRequested: () => Promise<void>;
}> = ({ result, hasPendingRequest, isCurrentMonth, onRequested }) => {
  const { showSuccess, showError } = useToast();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Un solo lugar para pedir: el mes en curso, donde sigue sumando el acumulado. Las tarjetas de meses
  // cerrados nunca lo ofrecen (mostrarían el mismo acumulado y permitirían pedir dos veces).
  if (hasPendingRequest || !isCurrentMonth) return null;

  if (!result.earlyLiquidationEligible) {
    // No elegible: se explica la regla en vez de ocultar el botón sin decir nada.
    return (
      <p className="text-xs text-gray-500" data-testid="nz-liquidacion-anticipada-no-elegible">
        Podés pedir una liquidación anticipada desde el segundo mes seguido sin llegar a {NZ_MIN_THRESHOLD} sesiones.
      </p>
    );
  }
  if (result.earlyLiquidationAmount <= 0) return null;

  // Sesiones que faltan para la primera franja, sobre el acumulado NETO de la psicóloga (informativo).
  const nextTier = REMUNERACION_TIERS[0];
  const faltanParaTramo = Math.max(0, NZ_MIN_THRESHOLD - result.netConsideredForThreshold);

  const montoLabel = `USD ${result.earlyLiquidationAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const resp = await solicitarLiquidacionAnticipada({
        month: result.month,
        sessionsThisMonth: result.sessionsThisMonth,
        carriedOverFromPreviousMonths: result.carriedOverFromPreviousMonths,
        totalConsideredForThreshold: result.totalConsideredForThreshold,
        earlyLiquidationAmount: result.earlyLiquidationAmount,
      });
      if (resp.sent) {
        activityTracker.trackAction('advance_payment_requested'); // Métricas de Actividad: solo la solicitud enviada, sin montos
        showSuccess('Enviamos tu solicitud — te vamos a contactar para coordinar el pago.');
        setSent(true);
        setOpen(false);
        void onRequested();
      } else if (resp.reason === 'throttled') {
        // Ya se mandó el mail hace menos de 24hs (ver AnticipadaRequestLog) — no es un
        // error, así que no usamos showError: la solicitud anterior sigue en pie.
        showSuccess('Ya enviaste una solicitud para este período — te vamos a contactar pronto. Si pasó más de un día, escribinos directamente.');
        setSent(true);
        setOpen(false);
        void onRequested();
      } else {
        showError('No pudimos enviar tu solicitud. Escribinos por el canal de contacto habitual.');
      }
    } catch {
      showError('No pudimos enviar tu solicitud. Intentá de nuevo en unos minutos.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={sent}
        data-testid="nz-liquidacion-solicitar-anticipada-btn"
        className="w-full rounded-xl border border-violet-600 text-violet-600 px-4 py-2.5 text-sm font-semibold hover:bg-violet-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
      >
        {sent ? 'Solicitud enviada' : `Solicitar liquidación anticipada (${montoLabel})`}
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!submitting) setOpen(v); }}>
        <DialogContent data-testid="nz-liquidacion-solicitar-anticipada-modal" className="sm:max-w-[28rem]">
          <DialogHeader>
            <DialogTitle>¿Solicitar liquidación anticipada?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              Vas a pedir que se te liquiden <strong className="text-gray-900">{montoLabel}</strong> de tu
              acumulado actual ({result.netConsideredForThreshold} sesiones). El resto sigue acumulando
              para tu próxima liquidación normal.
            </p>
            {faltanParaTramo > 0 && nextTier && (
              <p className="text-sm text-gray-700" data-testid="nz-liquidacion-anticipada-modal-tramo">
                Pedir la anticipada descuenta esas sesiones del tramo. Hoy te {faltanParaTramo === 1 ? 'falta' : 'faltan'}{' '}
                {faltanParaTramo} {faltanParaTramo === 1 ? 'sesión' : 'sesiones'} para el tramo {nextTier.range} (
                {nextTier.amount} USD).
              </p>
            )}
            <p className="text-xs text-gray-500">
              Si antes de que cierre el mes llegás a {NZ_MIN_THRESHOLD} sesiones sin que se haya abonado, se
              liquida por tu franja y esta solicitud queda sin efecto. Lo que ya se abone como anticipada se
              descuenta, para no pagarlo dos veces.
            </p>
            <p className="text-xs text-gray-400">
              Es una solicitud, no un pago confirmado — te vamos a contactar para coordinarlo.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={submitting}>
              {submitting ? 'Enviando...' : 'Confirmar solicitud'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MetricsLiquidacionTab;
